import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'wm_cookie_consent';

// Site-wide cookie consent banner. Choice is remembered per browser and
// recorded server-side (with the cookie-policy version) for compliance.
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = async (action, analytics, marketing) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ action, essential: true, analytics, marketing, ts: new Date().toISOString() }));
    } catch { /* private browsing — banner may reappear */ }
    setVisible(false);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/consent/cookies`,
        { action, analytics, marketing },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
    } catch { /* consent already stored locally */ }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 p-4 sm:p-6" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto bg-[#141416] border border-white/10 rounded-2xl shadow-2xl p-5 sm:p-6 backdrop-blur">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-[#D4A24A] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-semibold">We value your privacy</p>
            <p className="text-xs text-white/60 mt-1 leading-relaxed">
              We use essential cookies to make this site work, and optional cookies to understand
              usage and improve your experience. See our{' '}
              <Link to="/legal/privacy-policy" className="text-[#D4A24A] hover:underline" data-testid="cookie-link-privacy">Privacy Policy</Link>,{' '}
              <Link to="/legal/cookie-policy" className="text-[#D4A24A] hover:underline" data-testid="cookie-link-cookie">Cookie Policy</Link> and{' '}
              <Link to="/legal/terms-of-service" className="text-[#D4A24A] hover:underline" data-testid="cookie-link-terms">Terms of Service</Link>.
            </p>

            {managing && (
              <div className="mt-4 space-y-3 border-t border-white/10 pt-4" data-testid="cookie-preferences-panel">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-white">Essential cookies</p>
                    <p className="text-[11px] text-white/40">Login sessions and security — always on.</p>
                  </div>
                  <Switch checked disabled data-testid="cookie-toggle-essential" />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-white">Analytics cookies</p>
                    <p className="text-[11px] text-white/40">Help us understand how the platform is used.</p>
                  </div>
                  <Switch
                    checked={prefs.analytics}
                    onCheckedChange={(v) => setPrefs({ ...prefs, analytics: v })}
                    data-testid="cookie-toggle-analytics"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-white">Marketing cookies</p>
                    <p className="text-[11px] text-white/40">Used to personalise offers and promotions.</p>
                  </div>
                  <Switch
                    checked={prefs.marketing}
                    onCheckedChange={(v) => setPrefs({ ...prefs, marketing: v })}
                    data-testid="cookie-toggle-marketing"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {managing ? (
                <Button
                  size="sm"
                  className="bg-[#D4A24A] text-black hover:bg-[#c2933f] text-xs"
                  onClick={() => persist('custom', prefs.analytics, prefs.marketing)}
                  data-testid="cookie-save-preferences-btn"
                >
                  Save preferences
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    className="bg-[#D4A24A] text-black hover:bg-[#c2933f] text-xs"
                    onClick={() => persist('accept_all', true, true)}
                    data-testid="cookie-accept-all-btn"
                  >
                    Accept All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white/80 text-xs"
                    onClick={() => persist('reject_optional', false, false)}
                    data-testid="cookie-reject-optional-btn"
                  >
                    Reject Optional Cookies
                  </Button>
                  <button
                    className="text-xs text-white/50 hover:text-white underline underline-offset-2 transition px-2"
                    onClick={() => setManaging(true)}
                    data-testid="cookie-manage-preferences-btn"
                  >
                    Manage Preferences
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
